import type { ProgrammaticLandingPageConfig } from '../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from './storageSizeFacts'

export const KWH_12_SPEICHER_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '12-kwh-speicher-kosten',
  lang: 'de',
  topicId: 'kwh-speicher-12',
  title: '12 kWh Batteriespeicher: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet ein 12-kWh-Batteriespeicher? Für wen sich diese Speichergröße eignet, ' +
    'Eigenverbrauchs-Richtwerte und kostenloser Preisvergleich für Anlage + Speicher.',
  keywords: [
    '12 kWh Speicher Kosten',
    'Batteriespeicher 12 kWh Preis',
    '12 kWh Solarspeicher',
    'Was kostet ein 12 kWh Speicher',
  ],
  eyebrow: 'Speichergröße 12 kWh',
  h1: '12 kWh Batteriespeicher: Kosten und Richtwerte',
  intro:
    'Ein 12-kWh-Speicher passt zu Haushalten mit etwas höherem Verbrauch als beim ' +
    `Standardfall, kombiniert mit einer ${STORAGE_REFERENCE_KWP}-kWp-Referenzanlage. Hier finden ` +
    'Sie Richtwerte zur passenden Haushaltsgröße, zum Eigenverbrauch mit Speicher und direkt ' +
    'darunter einen Rechner mit dem für diese Kombination vorbelegten Durchschnittspreis aus ' +
    'echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 12 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich ein 12-kWh-Speicher?' },
    {
      type: 'paragraph',
      text: 'Richtwert: 12 kWh eignen sich für Haushalte mit erhöhtem Abendverbrauch oder ' +
        'gelegentlichem E-Auto-Laden. Bei fest eingeplanter ' +
        'Wärmepumpe oder täglichem E-Auto-Laden ist meist 15 oder 20 kWh die passendere Stufe.',
    },
    { type: 'heading', text: 'Eigenverbrauch mit Batteriespeicher' },
    {
      type: 'paragraph',
      text:
        `Ohne Speicher liegt der Eigenverbrauchsanteil bei einer ${STORAGE_REFERENCE_KWP}-kWp-Anlage ` +
        `typischerweise bei rund ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %, mit Batteriespeicher ` +
        `im Rechenmodell rund ${SELF_CONSUMPTION_BATTERY_BONUS * 100} Prozentpunkte höher bei etwa ` +
        `${SELF_CONSUMPTION_WITH_BATTERY * 100} % - ein zusätzliches E-Auto-Ladeprofil kann den ` +
        'Anteil bis zu einer Obergrenze von 85 % weiter steigern. Eine genaue, auf Ihren Verbrauch ' +
        'zugeschnittene Berechnung liefert der PV-Rechner.',
    },
    { type: 'heading', text: 'Notstromfähigkeit' },
    {
      type: 'paragraph',
      text:
        'Bei 12 kWh reicht die Kapazität vieler Notstrom-Backup-Boxen für einen deutlich größeren ' +
        'Teil des Haushalts über einen längeren Zeitraum - wie lange genau, hängt vom tatsächlichen ' +
        'Verbrauch der angeschlossenen Geräte ab. Ob und in welchem Umfang ein Angebot ' +
        'Notstromfähigkeit einschließt, ist je Handwerker unterschiedlich - gezielt beim Angebot ' +
        'nachfragen.',
    },
    { type: 'heading', text: 'Speicherkosten und Förderung' },
    {
      type: 'paragraph',
      text:
        'Speicherkosten schwanken je nach Hersteller und Installationsaufwand stark - Marktrecherche ' +
        `2026 zeigt eine Spanne von etwa ${BATTERY_COST_PER_KWH_RANGE_EUR.min} bis ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('de-DE')} € pro kWh. Speicherspezifische ` +
        'Förderungen gibt es regional unterschiedlich (z. B. Berliner Landesprogramm mit Zuschuss ' +
        'für Batteriespeicher) - Details im Förder-Überblick, eine genaue Berechnung für Ihre ' +
        'Kombination liefert der Rechner unten.',
    },
  ],
  faq: [
    {
      q: 'Wann ist 12 kWh statt 10 kWh die bessere Wahl?',
      a: 'Bei erhöhtem Abendverbrauch oder wenn gelegentlich ein E-Auto zuhause geladen wird, deckt 12 kWh den zusätzlichen Bedarf meist besser ab als 10 kWh.',
    },
    {
      q: 'Lohnt sich 12 kWh ohne Wärmepumpe oder E-Auto?',
      a: 'Meist nicht optimal ausgelastet - ohne diese Großverbraucher ist 10 kWh für die meisten Haushalte ausreichend. Eine genaue Empfehlung für Ihren Verbrauch liefert der PV-Rechner.',
    },
    {
      q: 'Mit welcher Anlagengröße wird ein 12-kWh-Speicher meist kombiniert?',
      a: `Auf dieser Seite als Referenz eine ${STORAGE_REFERENCE_KWP}-kWp-Anlage, in der Praxis oft auch mit 12 kWp kombiniert - eine genaue Empfehlung für Ihr Dach liefert der PV-Rechner.`,
    },
    {
      q: 'Reicht 12 kWh, um über Nacht komplett autark zu sein?',
      a: 'Ja, in den meisten Haushalten auch mit überdurchschnittlichem Verbrauch. Eine genaue, auf Ihren tatsächlichen Verbrauch zugeschnittene Einschätzung liefert der PV-Rechner.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '10 kWh Batteriespeicher: Kosten & Richtwerte', href: '/10-kwh-speicher-kosten' },
    { label: '15 kWh Batteriespeicher: Kosten & Richtwerte', href: '/15-kwh-speicher-kosten' },
    { label: '12 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/12-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
